import React, { useEffect, useState } from 'react'
import {
	AppointmentsContainer,
	AppointmentCard,
	AppointmentInfo,
	DateBadge,
	DateDay,
	DateTime,
	AppointmentType,
	Professional,
	ClinicName,
	AppointmentsWrapper,
	AppointmentCarConteiner,
	ButtonContainer,
	VerticalDivider,
	InfoRow,
	StatusBadge,
	DetailFooter
} from './styled'
import moment from 'moment'
import 'moment/locale/es'
import { capitalize } from '../../utils/stringsUtils'
import { Button } from '../ui/button'
import useIsMobile from '../../hooks/useIsMobile'
import { Calendar, ErrorCircle } from '../../assets/icons'
import AddToCalendarQuickAccessModal from '../AddToCalendarQuickAccessModal'
import CancelAppointmentConfirmModal from '../CancelAppointmentConfirmModal'
import theme from '../../styled/theme'
import config from '../../config'
import { useHistory } from 'react-router-dom'
import { buildAppointmentCalendarEvent } from '../../utils/appointmentCalendarEvent'
import { getAppointmentHistoryStatus } from '../../utils/appointmentStatus'
import to from 'await-catch'
import { cancelAppointment } from '../../api'

moment.locale('es')
moment.updateLocale('es', {
	monthsShort: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
})

const CANCEL_THRESHOLD = config.cancelThresholdHours

const UpcomingAppointmentsCard = ({
	appointments,
	isCarousel,
	showButtons = true,
	sortDirection = 'asc',
	isHistory = false,
	setShowFeedback
}) => {
	const history = useHistory()
	const isMobile = useIsMobile()
	const [showCancelModal, setShowCancelModal] = useState(false)
	const [showAddToCalendarInfo, setShowAddToCalendarInfo] = useState(false)
	const [selectedAppointment, setSelectedAppointment] = useState(null)
	const [appointmentsList, setAppointmentsList] = useState(appointments || [])

	useEffect(() => {
		const direction = sortDirection === 'desc' ? -1 : 1
		const dateKey = isHistory ? 'fechaAgenda' : 'fechaTurnoDateTime'
		const sortedAppointments = appointments?.sort(
			(a, b) => direction * (new Date(a[dateKey]) - new Date(b[dateKey]))
		)

		setAppointmentsList(sortedAppointments || [])
	}, [appointments, sortDirection, isHistory])

	const openModal = setModalState => appointment => {
		setSelectedAppointment(appointment)
		setModalState(true)
	}

	const closeModal = setModalState => () => {
		setModalState(false)
		setSelectedAppointment(null)
	}

	const openCancelModal = openModal(setShowCancelModal)
	const closeCancelModal = closeModal(setShowCancelModal)
	const openAddToCalendarModal = openModal(setShowAddToCalendarInfo)
	const closeAddToCalendarModal = closeModal(setShowAddToCalendarInfo)

	const handleViewDetails = appointment => {
		history.push('/inicio/mis-turnos/detalle', { appointment })
	}

	const confirmCancelAppointment = async () => {
		if (!selectedAppointment) {
			return
		}
		const appointmentId = selectedAppointment.idTurno
		const clinicCode = selectedAppointment.codigoEmpresa
		const [err] = await to(cancelAppointment(appointmentId, clinicCode, 1))
		if (!err) {
			setShowFeedback({ show: true, success: true })
		} else {
			setShowFeedback({ show: true, success: false })
		}
		closeCancelModal()
	}

	const selectedCalendarEvent = buildAppointmentCalendarEvent(selectedAppointment)

	return (
		<>
			<CancelAppointmentConfirmModal
				isOpen={showCancelModal}
				onClose={closeCancelModal}
				onConfirm={confirmCancelAppointment}
				isFullHeight={isMobile}
			/>
			<AddToCalendarQuickAccessModal
				isOpen={showAddToCalendarInfo}
				onClose={closeAddToCalendarModal}
				isFullHeight={isMobile}
				event={selectedCalendarEvent}
			/>
			<AppointmentsWrapper>
				<AppointmentsContainer isCarousel={isCarousel}>
					{appointmentsList?.map(appointment => {
						const {
							id,
							idTurno,
							fechaTurnoDateTime,
							clinica,
							nombreProfesional,
							tipo = 'Presencial'
						} = appointment
						const appointmentDate = isHistory ? appointment.fechaAgenda : fechaTurnoDateTime
						const canCancel =
							moment(fechaTurnoDateTime).diff(moment(), 'hours', true) >=
							CANCEL_THRESHOLD
						const appointmentKey = idTurno || id || `${appointmentDate}-${nombreProfesional}`
						const status = isHistory ? getAppointmentHistoryStatus(appointment.estado) : null

						return (
							<AppointmentCarConteiner key={appointmentKey}>
								<AppointmentCard
									$isHistory={isHistory}
									$hasBottomBR={!isHistory && !showButtons}
									onClick={() => handleViewDetails(appointment)}
									onKeyDown={event => {
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault()
											handleViewDetails(appointment)
										}
									}}
									tabIndex={0}
									role="button"
								>
									<DateBadge 	$isHistory={isHistory}>
										<DateDay>{moment(appointmentDate).format('D MMM')}</DateDay>
										<DateTime>{moment(appointmentDate).format('LT')}</DateTime>
									</DateBadge>
									<AppointmentInfo>
										{isHistory ? (
											<>
												<div>
													<ClinicName>{capitalize(nombreProfesional, true)}</ClinicName>
													<Professional>{appointment.especialidad}</Professional>
												</div>
												<InfoRow>
													<AppointmentType>{appointment.establecimiento}</AppointmentType>
													{status && (
														<StatusBadge $color={status.color}>
															{status.showIcon && <ErrorCircle size={16} color={status.color} />}
															{status.label}
														</StatusBadge>
													)}
												</InfoRow>
											</>
										) : (
											<>
												<div>
													<ClinicName>{clinica}</ClinicName>
													<Professional>
														Profesional: {capitalize(nombreProfesional, true)}
													</Professional>
												</div>
												<InfoRow>
													<AppointmentType>{tipo}</AppointmentType>
												</InfoRow>
											</>
										)}
									</AppointmentInfo>
								</AppointmentCard>
								{  showButtons && (
									<ButtonContainer justifyContent="left">
										{!isHistory && (<Button
											iconLeft={<Calendar color={theme.colors.brand.blueOmint} />}
											$showBorder={false}
											$mt={'0px'}
											label={'Agregar a calendario'}
											$variant="tertiary"
											size={isMobile ? 'mobile' : 'xlarge'}
											$underline={true}
											onClick={() => openAddToCalendarModal(appointment)}
										/>)}
										{isHistory && (
											<Button
													$showBorder={false}
													$mt={'0px'}
													label={'Ver detalles'}
													$variant="tertiary"
													size={isMobile ? 'mobile' : 'xlarge'}
													$underline={true}
													onClick={() => handleViewDetails(appointment)}
												/>
										)}
										{!isHistory && canCancel && (
											<>
												<VerticalDivider />
												<Button
													$showBorder={false}
													$mt={'0px'}
													label={'Cancelar turno'}
													$variant="tertiary"
													size={isMobile ? 'mobile' : 'xlarge'}
													$underline={true}
													onClick={() => openCancelModal(appointment)}
												/>
											</>
										)}
									</ButtonContainer>
								)}
							</AppointmentCarConteiner>
						)
					})}
				</AppointmentsContainer>
			</AppointmentsWrapper>
		</>
	)
}

export default UpcomingAppointmentsCard
